/**
 * Centralised constants for headline figure keys.
 *
 * These keys correspond to the values returned by the backend in the
 * `allowedFigures: [String!]!` field on headline types (installation,
 * system, asset). Keeping them in one place avoids scattered magic strings
 * and makes it easy to add/remove figures across the platform.
 */

export const FIGURE_KEYS = {
  ALERTS: "alerts",
  NEXT_REFUEL: "next_refuel",
  HYDROGEN_LEVEL: "hydrogen_level",
  HYDROGEN_USED: "hydrogen_used",
  ENERGY: "energy",
  EMISSIONS_FACTOR: "emissions_factor",
  CURRENT_POWER: "current_power",
  CO2E: "co2e",
  CURRENT_LEVEL: "current_level",
  STATE_OF_CHARGE: "state_of_charge",
  GENERATOR_RUNTIME: "generator_runtime",
  HYBRID_LOAD: "hybrid_load",
  HYBRID_GENERATION: "hybrid_generation",
  HYBRID_FUEL_LEVEL: "hybrid_fuel_level",
  HYBRID_BATTERY_LEVEL: "hybrid_battery_level",
  HYBRID_FUEL_SAVED: "hybrid_fuel_saved",
  // --- Burnaston overview cards (see docs/current/burnaston-cards) ---
  VEHICLES_TO_FILL: "vehicles_to_fill",
  ELECTROLYSER_STATUS: "electrolyser_status",
  HOURS_TO_FILL: "hours_to_fill",
  HYDROGEN_PRODUCED: "hydrogen_produced",
} as const

export type FigureKey = (typeof FIGURE_KEYS)[keyof typeof FIGURE_KEYS]

/**
 * Maps schema-level FigureType values (used in installation page JSON schema)
 * to the corresponding backend `allowedFigures` key.
 */
export const FIGURE_TYPE_TO_KEY: Record<string, FigureKey | undefined> = {
  HydrogenFuelForecasting: FIGURE_KEYS.NEXT_REFUEL,
  HydrogenFuelLevel: FIGURE_KEYS.HYDROGEN_LEVEL,
  WeightDifference: FIGURE_KEYS.HYDROGEN_USED,
  Usage: FIGURE_KEYS.ENERGY,
  EmissionsFactor: FIGURE_KEYS.EMISSIONS_FACTOR,
  CO2e: FIGURE_KEYS.CO2E,
  CurrentPower: FIGURE_KEYS.CURRENT_POWER,
  CurrentLoad: FIGURE_KEYS.HYBRID_LOAD,
  // --- Burnaston overview cards (see docs/current/burnaston-cards) ---
  VehiclesToFill: FIGURE_KEYS.VEHICLES_TO_FILL,
  ElectrolyserStatus: FIGURE_KEYS.ELECTROLYSER_STATUS,
  HoursToFill: FIGURE_KEYS.HOURS_TO_FILL,
  HydrogenProduced: FIGURE_KEYS.HYDROGEN_PRODUCED,
}

/**
 * Default fallback data for each figure type when the backend returns null.
 * Used to render placeholder/empty-state figures in the UI.
 */
export interface FigureFallback {
  value: null
  unit: string | null
  label: string
  available: false
  metadata?: Record<string, unknown>
}

export const FIGURE_DEFAULTS: Record<FigureKey, FigureFallback> = {
  [FIGURE_KEYS.ALERTS]: { value: null, unit: null, label: "Alerts", available: false, metadata: {} },
  [FIGURE_KEYS.NEXT_REFUEL]: { value: null, unit: null, label: "Asset Depleted in", available: false, metadata: {} },
  [FIGURE_KEYS.HYDROGEN_LEVEL]: { value: null, unit: null, label: "Hydrogen level", available: false, metadata: {} },
  [FIGURE_KEYS.HYDROGEN_USED]: { value: null, unit: null, label: "Hydrogen used", available: false, metadata: {} },
  [FIGURE_KEYS.ENERGY]: { value: null, unit: "kWh", label: "Energy used", available: false },
  [FIGURE_KEYS.EMISSIONS_FACTOR]: { value: null, unit: null, label: "Emissions factor", available: false, metadata: {} },
  [FIGURE_KEYS.CURRENT_POWER]: { value: null, unit: "kW", label: "Current Power", available: false },
  [FIGURE_KEYS.CO2E]: { value: null, unit: "kg CO2e", label: "CO2 emissions", available: false },
  [FIGURE_KEYS.CURRENT_LEVEL]: { value: null, unit: null, label: "", available: false, metadata: {} },
  [FIGURE_KEYS.STATE_OF_CHARGE]: { value: null, unit: "%", label: "State of Charge", available: false },
  [FIGURE_KEYS.GENERATOR_RUNTIME]: { value: null, unit: "%", label: "Generator Runtime", available: false, metadata: {} },
  [FIGURE_KEYS.HYBRID_LOAD]: { value: null, unit: "kW", label: "Current Load", available: false },
  [FIGURE_KEYS.HYBRID_GENERATION]: { value: null, unit: "kW", label: "Current Generation", available: false },
  [FIGURE_KEYS.HYBRID_FUEL_LEVEL]: { value: null, unit: "%", label: "Fuel Level", available: false },
  [FIGURE_KEYS.HYBRID_BATTERY_LEVEL]: { value: null, unit: "%", label: "Battery Level", available: false },
  [FIGURE_KEYS.HYBRID_FUEL_SAVED]: { value: null, unit: "L", label: "Savings", available: false, metadata: {} },
  // --- Burnaston overview cards (see docs/current/burnaston-cards) ---
  [FIGURE_KEYS.VEHICLES_TO_FILL]: { value: null, unit: null, label: "Fill Capacity", available: false, metadata: {} },
  [FIGURE_KEYS.ELECTROLYSER_STATUS]: { value: null, unit: null, label: "Electrolyser Status", available: false, metadata: {} },
  [FIGURE_KEYS.HOURS_TO_FILL]: { value: null, unit: null, label: "Time to Fill Storage", available: false, metadata: {} },
  [FIGURE_KEYS.HYDROGEN_PRODUCED]: { value: null, unit: "kg", label: "Hydrogen Produced", available: false },
}
